import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Text,
  useColorMode,
  useColorModeValue,
  Input,
  Button,
  VStack,
  HStack,
  Icon,
  IconButton as ChakraIconButton,
  Switch,
  FormControl,
  FormLabel,
  useDisclosure, // Added
  Badge,         // Added
  Wrap,          // Added
  WrapItem,      // Added
  Fade,          // Added
  Grid,          // Added
  GridItem,      // Added
  Modal,         // Added
  ModalOverlay,  // Added
  ModalContent,  // Added
  ModalHeader,   // Added
  ModalFooter,   // Added
  ModalBody,     // Added
  ModalCloseButton, // Added
  Tooltip,       // Added
  Text as ChakraText, // Added for clarity in modal
  Heading as ChakraHeading, // Added for clarity in modal
  Tag,           // Added for active filter display
} from '@chakra-ui/react';
import {
  GoogleMap,
  MarkerClusterer,
} from '@react-google-maps/api';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { Funnel, CaretUp, CaretDown, ArrowsOutSimple, ArrowsInSimple, MagnifyingGlass, X, Info } from '@phosphor-icons/react'; // Added X and Info
import { CloseIcon } from '@chakra-ui/icons';
import SiteMarker from '../components/SiteMarker';
import SubLocationMarker from '../components/SubLocationMarker'; 
import SiteCard from '../components/SiteCard';
import SubLocationInfoWindow from '../components/SubLocationInfoWindow'; 
import ElasticSearchFilters from '../components/ElasticSearchFilters'; // Added
import ImagePreviewModal from '../components/ImagePreviewModal'; // Added for sublocation image preview
import { searchSites, transformSearchResults, transformSearchParams, extractFilterOptions } from '../utils/elasticsearchApi'; // Added transformSearchParams, extractFilterOptions
import { getMediaFeatures, generateFeatureVariations } from '../utils/featureUtils';
import MapLayerToggle from '../components/maps/MapLayerToggle';
import '../components/maps/MapStyles.css'; 
import '../styles/mobile-map-optimizations.css';
import { useAuth } from '../hooks/useAuth'; 

// Styles copied from Map.jsx - consider moving to a shared file if not already
const getMapContainerStyle = (colorMode, isFullscreen) => ({
  width: '100%',
  height: '100%',
  border: colorMode === 'dark' ? '1px solid #4A5568' : 'none',
  borderRadius: isFullscreen ? 0 : 'md',
  overflow: 'hidden',
  _after: {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    opacity: 0.08,
    bgImage: colorMode === 'light'
      ? 'var(--texture-canvas)'
      : 'var(--texture-leather)',
    bgRepeat: 'repeat',
    bgSize: '300px',
    bgBlendMode: colorMode === 'light' ? 'soft-light' : 'multiply',
  }
});

const mapStyles = {
  light: [
    { elementType: 'geometry', stylers: [{ color: '#EBDEC2' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#F5F5F5' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#3B2E1E' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#6A4E33' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6B573B' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#D9CAA7' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6B573B' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#FDF6E3' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#C0AD89' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6B573B' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#F8EDD8' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#9C826B' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#E6DAC0' }] },
    { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#6A4E33' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#A5C9D1' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3B2E1E' }] },
    { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#FFFFFF' }] },
  ],
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#38291E' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#2C231B' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#D7D0C0' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#D6AD60' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#B7A58B' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#483123' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#B7A58B' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#483123' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#50392A' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#B7A58B' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#50392A' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#6A4E33' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#483123' }] },
    { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#D6AD60' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#233947' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#D7D0C0' }] },
    { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#2C231B' }] },
  ],
};

const MapElasticPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    materialType: '',
    features: [],
    era: '',
    verificationStatus: '',
    countries: [],
    featureFilterLogic: 'AND',
  });
  const [filterOptions, setFilterOptions] = useState({});
  const [featuresMetadata, setFeaturesMetadata] = useState({});
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const { isOpen: isInfoModalOpen, onOpen: onInfoModalOpen, onClose: onInfoModalClose } = useDisclosure();
  const { isOpen: isImagePreviewOpen, onOpen: onImagePreviewOpen, onClose: onImagePreviewClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null); // Added for image preview
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Added for image navigation
  const [sublocationImages, setSublocationImages] = useState([]); // Added for image collection
  const [error, setError] = useState(null);
  const [center, setCenter] = useState({ lat: 20, lng: 0 });
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedSublocation, setSelectedSublocation] = useState(null); // Added
  const [mapInstance, setMapInstance] = useState(null);
  const [mapTypeId, setMapTypeId] = useState('terrain');
  const [showLabels, setShowLabels] = useState(false);
  const [isCustomFullscreen, setIsCustomFullscreen] = useState(false);
  const [showSublocations, setShowSublocations] = useState(false); // Added for sublocation toggle - default off
  const [allSublocationsCache, setAllSublocationsCache] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [touchStartY, setTouchStartY] = useState(null);

  const { colorMode } = useColorMode();
  const bgColor = useColorModeValue('gray.50', 'gray.900'); // For filter panel
  const { user } = useAuth();

  // Mobile detection effect
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
 
  const mapContainerStyle = useMemo(() => getMapContainerStyle(colorMode, isCustomFullscreen), [colorMode, isCustomFullscreen]);
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: isMobile,
    mapTypeControl: false,
    fullscreenControl: false,
    styles: colorMode === 'dark' ? mapStyles.dark : mapStyles.light,
  }), [colorMode, isMobile]);

  const performSearch = useCallback(async (currentQuery = '', currentFilters, initialLoad = false) => {
    setIsLoading(true);
    setError(null);
    console.log(`[MapElasticPage] ${initialLoad ? 'Initial load' : 'Searching'} for: "${currentQuery}" with filters:`, currentFilters);
    try {
      const searchParameters = transformSearchParams({ get: (param) => param === 'name' ? currentQuery : null }, currentFilters);
      // Ensure 'size' is appropriate for map view, e.g., fetching more results
      searchParameters.size = 500; 
      
      console.log('[MapElasticPage] Elasticsearch search parameters:', searchParameters);
      const response = await searchSites(searchParameters);
      console.log('[MapElasticPage] Raw Elasticsearch response:', response);

      if (response.success) {
        const transformed = transformSearchResults(response.data);
        console.log('[MapElasticPage] Transformed search results:', transformed);
        setSearchResults(transformed);

        const options = extractFilterOptions(response.aggregations);
        setFilterOptions(options);

        // Build features metadata for compatibility, ensuring category is included
        const featureMap = new Map();
        if (options.features) {
          options.features.forEach(feature => { // feature from options.features should have id, label, category
            featureMap.set(feature.id, { 
              id: feature.id, 
              label: feature.label, 
              category: feature.category // Include category here
            });
          });
        }
        setFeaturesMetadata(Object.fromEntries(featureMap));

        if (transformed.length > 0 && transformed[0].location?.coordinates) {
          if (initialLoad || currentQuery) {
             setCenter(transformed[0].location.coordinates);
          }
        } else if (transformed.length === 0 && currentQuery) {
          console.log('[MapElasticPage] Search returned no results for query:', currentQuery);
        }
      } else {
        setError(response.error || 'Search failed');
        setSearchResults([]);
        console.error('[MapElasticPage] Search failed:', response.error);
      }
    } catch (err) {
      console.error('[MapElasticPage] Error fetching from Elasticsearch:', err);
      setError('An error occurred while searching.');
      setSearchResults([]);
    }
    setIsLoading(false);
  }, []); // Removed transformSearchParams, extractFilterOptions from deps as they are stable imports

  // Effect for initial data load & filter changes (excluding searchQuery to prevent search on every keystroke)
  useEffect(() => {
    if (initialLoadComplete) {
      performSearch(searchQuery, filters);
    }
  }, [filters, initialLoadComplete, performSearch]);

  // Effect for initial load only
  useEffect(() => {
    performSearch('', filters, true).then(() => {
      setInitialLoadComplete(true);
    });
  }, []); // Empty dependency array for mount-only effect



  const handleSearch = useCallback(() => { 
    performSearch(searchQuery, filters, false);
  }, [performSearch, searchQuery, filters]);
  
  // Filter handling functions
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const resetFilters = () => {
    setFilters({
      materialType: '', features: [], era: '', verificationStatus: '', countries: [], featureFilterLogic: 'AND',
    });
  };

  const handleFeatureToggle = (featureOrId) => {
    const rawId = typeof featureOrId === 'string' ? featureOrId : featureOrId.id;
    setFilters(prev => {
      const currentFeatures = prev.features || [];
      const exists = currentFeatures.includes(rawId);
      const newFeatures = exists ? currentFeatures.filter(id => id !== rawId) : [...currentFeatures, rawId];
      return { ...prev, features: newFeatures };
    });
  };

  const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    if (key === 'featureFilterLogic') return count;
    if (Array.isArray(value)) return count + value.length;
    if (value) return count + 1;
    return count;
  }, 0);
  
  const getFeatureLabel = (featureId) => {
    return featuresMetadata[featureId]?.label || featureId;
  };

  const getFilteredMedia = useCallback((allMedia) => {
    if (!Array.isArray(allMedia)) return [];
    
    console.log('🗺️ [getFilteredMedia] Starting with media:', allMedia.length);
    console.log('🗺️ [getFilteredMedia] Active filters:', filters.features);
    
    let imageMedia = allMedia.filter(media => media && (media.type === 'image' || media.type === 'video') && media.url && media.url.match(/^https?:\/\/.+/));
    
    console.log('🗺️ [getFilteredMedia] Valid image/video media:', imageMedia.length);
    
    if (filters.features && filters.features.length > 0) {
      // Create a map of normalized features to search for
      const searchFeatureVariations = new Map();
      filters.features.forEach(feature => {
        const variations = generateFeatureVariations(feature);
        console.log(`🔄 [getFilteredMedia] Generated variations for "${feature}":`, variations);
        variations.forEach(variation => {
          searchFeatureVariations.set(variation.toLowerCase(), feature);
        });
      });

      console.log('🗺️ [getFilteredMedia] Search feature variations map:', searchFeatureVariations);

      imageMedia = imageMedia.filter((media, index) => {
        // Get all features for this media item using our utility
        const mediaFeatures = getMediaFeatures(media);
        const mediaFeatureIds = new Set(mediaFeatures.map(f => f.id.toLowerCase()));

        console.log(`📷 [getFilteredMedia] Media item ${index} (${media.id}):`, {
          features: mediaFeatures,
          featureIds: Array.from(mediaFeatureIds)
        });

        // Check if media matches any of the search feature variations
        const matchingFeatures = new Set();
        for (const mediaFeatureId of mediaFeatureIds) {
          if (searchFeatureVariations.has(mediaFeatureId)) {
            matchingFeatures.add(searchFeatureVariations.get(mediaFeatureId));
            console.log(`✅ [getFilteredMedia] Match found: "${mediaFeatureId}" matches filter "${searchFeatureVariations.get(mediaFeatureId)}"`);
          }
        }

        // Apply AND/OR logic
        let matchesFilter = false;
        if (filters.featureFilterLogic === 'OR') {
          // OR: media must have at least one of the selected features
          matchesFilter = matchingFeatures.size > 0;
        } else {
          // AND: media must have all of the selected features
          matchesFilter = filters.features.every(feature => matchingFeatures.has(feature));
        }

        console.log(`🎯 [getFilteredMedia] Media item ${index} matches filter:`, matchesFilter, 'matching features:', Array.from(matchingFeatures));

        return matchesFilter;
      });
    }
    
    console.log('✅ [getFilteredMedia] Final filtered media count:', imageMedia.length);
    return imageMedia;
  }, [filters.features, filters.featureFilterLogic]);

  const handleMarkerClick = useCallback(async (site) => {
    if (!site?.id) return;
    // Store the site with its original media. Filtering for display will be handled by SiteCard via props.
    setSelectedSite({
      ...site, // site object already contains its media array
    });
    setSelectedSublocation(null); 
  }, []);
  
  const handleSublocationMarkerClick = useCallback((sublocation) => {
    setSelectedSublocation(sublocation);
    setSelectedSite(null); // Clear selected site when a sublocation is clicked
  }, []);

  const handleMapClick = useCallback(() => {
    setSelectedSite(null);
    setSelectedSublocation(null); // Clear selected sublocation on map click
  }, []);

  const handleMapTypeChange = useCallback(() => {
    setMapTypeId(prev => (prev === 'terrain' ? 'hybrid' : prev === 'hybrid' ? 'satellite' : 'terrain'));
  }, []);

  const handleToggleLabels = useCallback(() => setShowLabels(prev => !prev), []);
  const handleToggleFullscreen = useCallback(() => setIsCustomFullscreen(prev => !prev), []);
  
  useEffect(() => {
    if (mapInstance && isCustomFullscreen) {
      const timer = setTimeout(() => {
        window.google.maps.event.trigger(mapInstance, 'resize');
        mapInstance.setCenter(center);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCustomFullscreen, mapInstance, center]);

  // Effect for fetching sublocations cache
  useEffect(() => {
    const fetchSublocationsCache = async () => {
      if (showSublocations) {
        try {
          // console.log('[MapElasticPage] Attempting to fetch sublocations cache as showSublocations is true.');
          const firestoreInstance = getFirestore();
          const docRef = doc(firestoreInstance, 'system_data', 'all_sublocations_cache');
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data && Array.isArray(data.sublocations_array)) {
              setAllSublocationsCache(data.sublocations_array);
              // console.log(`[MapElasticPage] Sublocations cache fetched: ${data.sublocations_array.length} items.`);
            } else {
              // console.warn('[MapElasticPage] sublocations_array not found or not an array in cache document.');
              setAllSublocationsCache([]);
            }
          } else {
            // console.log('[MapElasticPage] No sublocations cache document found at system_data/all_sublocations_cache.');
            setAllSublocationsCache([]);
          }
        } catch (err) {
          // console.error('[MapElasticPage] Error fetching sublocations cache:', err);
          setAllSublocationsCache([]); 
        }
      } else {
        setAllSublocationsCache([]); 
        // console.log('[MapElasticPage] showSublocations is false, clearing sublocations cache.');
      }
    };

    fetchSublocationsCache();
  }, [showSublocations]);

  const MemoizedMarkers = React.memo(({ sites, onSiteClick, onSublocationClick, showLabels, showSublocations, allSublocationsCache, activeFilters }) => {
    // Helper function to check if a sublocation's media matches active feature filters
    const sublocationHasMatchingMedia = (sublocation, currentFilters) => {
      if (!currentFilters.features || currentFilters.features.length === 0) {
        return true; // No feature filter active, so all sublocations are considered matching
      }
      
      console.log('🎯 [sublocationHasMatchingMedia] Checking sublocation:', sublocation.sublocationId, 'with filters:', currentFilters.features);
      
      // Ensure sublocation.media exists and is an array.
      const sublocationMedia = sublocation.media || []; 
      if (!Array.isArray(sublocationMedia) || sublocationMedia.length === 0) {
        console.log('🎯 [sublocationHasMatchingMedia] No media found, showing sublocation');
        return true; // Show sublocations with no media when no filters are active
      }

      // Create a map of normalized features to search for
      const searchFeatureVariations = new Map();
      currentFilters.features.forEach(feature => {
        const variations = generateFeatureVariations(feature);
        console.log(`🔄 [sublocationHasMatchingMedia] Generated variations for "${feature}":`, variations);
        variations.forEach(variation => {
          searchFeatureVariations.set(variation.toLowerCase(), feature);
        });
      });

      console.log('🗺️ [sublocationHasMatchingMedia] Search feature variations map:', searchFeatureVariations);

      // Check if any media item matches the filter criteria
      const hasMatchingMedia = sublocationMedia.some((mediaItem, index) => {
        if (!mediaItem) return false;
        
        // Get all features for this media item using our utility
        const mediaFeatures = getMediaFeatures(mediaItem);
        
        // Skip media items with no features when filters are active
        if (!mediaFeatures || mediaFeatures.length === 0) {
          console.log(`📷 [sublocationHasMatchingMedia] Media item ${index} has no features, skipping`);
          return false; 
        }
        
        const mediaFeatureIds = new Set(mediaFeatures.map(f => f.id.toLowerCase()));

        console.log(`📷 [sublocationHasMatchingMedia] Media item ${index} features:`, Array.from(mediaFeatureIds));

        // Check if media matches any of the search feature variations
        const matchingFeatures = new Set();
        for (const mediaFeatureId of mediaFeatureIds) {
          if (searchFeatureVariations.has(mediaFeatureId)) {
            matchingFeatures.add(searchFeatureVariations.get(mediaFeatureId));
            console.log(`✅ [sublocationHasMatchingMedia] Match found: "${mediaFeatureId}" matches filter "${searchFeatureVariations.get(mediaFeatureId)}"`);
          }
        }

        // Apply AND/OR logic
        let matchesFilter = false;
        if (currentFilters.featureFilterLogic === 'OR') {
          // OR: media must have at least one of the selected features
          matchesFilter = matchingFeatures.size > 0;
        } else {
          // AND: media must have all of the selected features
          matchesFilter = currentFilters.features.every(feature => matchingFeatures.has(feature));
        }

        console.log(`🎯 [sublocationHasMatchingMedia] Media item ${index} matches filter:`, matchesFilter, 'matching features:', Array.from(matchingFeatures));

        return matchesFilter;
      });

      // Show sublocation if it has at least one matching media item OR if it has media without features
      const hasMediaWithoutFeatures = sublocationMedia.some(mediaItem => {
        const mediaFeatures = getMediaFeatures(mediaItem);
        return mediaItem && (!mediaFeatures || mediaFeatures.length === 0);
      });

      const shouldShow = hasMatchingMedia || hasMediaWithoutFeatures;
      console.log(`✅ [sublocationHasMatchingMedia] Sublocation ${sublocation.sublocationId} should show:`, shouldShow);
      
      return shouldShow;
    };
    
    // console.log(`[MemoizedMarkers] Rendering with activeFilters:`, activeFilters);
    return sites.flatMap((site) => {
      const siteMarkers = [];
      if (site.location?.coordinates) {
        siteMarkers.push(
          <SiteMarker
            key={site.id}
            site={site}
            onClick={() => onSiteClick(site)}
            // clusterer={null} // clusterer prop is not used by SiteMarker if it's not passed to MarkerClusterer's child func
            showLabels={showLabels}
            isMainMap={true}
          />
        );
      }

      if (showSublocations) {
        let sublocationsToDisplay = [];
        
        // PRIORITIZE CACHED SUBLOCATIONS (with enhanced media data) over search results
        if (allSublocationsCache && allSublocationsCache.length > 0) {
          // Use cached sublocations for this site (these have media arrays and features)
          sublocationsToDisplay = allSublocationsCache.filter(subloc => subloc.siteId === site.id);
          console.log(`[MapElastic] Site ${site.id} using ${sublocationsToDisplay.length} cached sublocations with media`);
        } else if (site.sublocations && Array.isArray(site.sublocations)) {
          // Fallback to search result sublocations (these are basic, no media)
          sublocationsToDisplay = site.sublocations.map(subloc => ({ ...subloc, siteId: site.id }));
          console.log(`[MapElastic] Site ${site.id} using ${sublocationsToDisplay.length} search result sublocations (no media)`);
        }

        sublocationsToDisplay.forEach((sublocation, index) => {
          if (sublocation.coordinates) {
            // Apply feature filtering to sublocations
            if (sublocationHasMatchingMedia(sublocation, activeFilters)) {
              siteMarkers.push(
                <SubLocationMarker
                  key={sublocation.sublocationId || sublocation.id || `${site.id}-subloc-${index}`}
                  sublocation={sublocation}
                  position={sublocation.coordinates}
                  onClick={() => onSublocationClick(sublocation)}
                  showLabels={showLabels}
                />
              );
            }
          } else {
            // console.warn(`[MapElastic] Sublocation ${sublocation.sublocationId || sublocation.id} has no coordinates:`, sublocation);
          }
        });
      }
      // console.log(`[MapElastic] Site ${site.id} total markers: ${siteMarkers.length}`);
      return siteMarkers;
    });
  });

  // Handle media click from sublocation info window to open image preview modal
  const handleMediaClick = (mediaItem) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[MapElasticPage] handleMediaClick received:', {
        hasSublocationPredictions: !!mediaItem.sublocationPredictions,
        sublocationPredictionsCount: mediaItem.sublocationPredictions?.length || 0,
        mediaItem,
      });
    }
    
    // Validate that the media item has a valid URL
    if (!mediaItem || !mediaItem.url) {
      console.error("Media item missing URL:", mediaItem);
      return;
    }
    
    // Ensure the URL is a valid HTTP/HTTPS URL
    if (!mediaItem.url.match(/^https?:\/\/.+/)) {
      console.error("Invalid media URL format:", mediaItem.url);
      return;
    }
    
    // Get all images from the current sublocation, applying filters if active
    if (selectedSublocation && selectedSublocation.media) {
      const filteredImages = getFilteredMedia(selectedSublocation.media);
      setSublocationImages(filteredImages);
      
      // Find the index of the clicked image in the filtered collection
      const imageIndex = filteredImages.findIndex(img => img.url === mediaItem.url);
      setSelectedImageIndex(imageIndex >= 0 ? imageIndex : 0);
    } else {
      setSublocationImages([mediaItem]);
      setSelectedImageIndex(0);
    }
    
    setSelectedImage(mediaItem);
    onImagePreviewOpen();
  };

  // Custom close handler for image preview modal that doesn't interfere with map state
  const handleImagePreviewClose = useCallback(() => {
    onImagePreviewClose();
    setSelectedImage(null);
    setSublocationImages([]);
    setSelectedImageIndex(0);
  }, [onImagePreviewClose]);

  // Navigation functions for image modal
  const handlePreviousImage = useCallback(() => {
    if (sublocationImages.length <= 1) return;
    
    const newIndex = selectedImageIndex === 0 ? sublocationImages.length - 1 : selectedImageIndex - 1;
    setSelectedImageIndex(newIndex);
    setSelectedImage(sublocationImages[newIndex]);
  }, [selectedImageIndex, sublocationImages]);

  const handleNextImage = useCallback(() => {
    if (sublocationImages.length <= 1) return;
    
    const newIndex = selectedImageIndex === sublocationImages.length - 1 ? 0 : selectedImageIndex + 1;
    setSelectedImageIndex(newIndex);
    setSelectedImage(sublocationImages[newIndex]);
  }, [selectedImageIndex, sublocationImages]);

  return (
    <Box
      p={isCustomFullscreen ? 0 : 4}
      bg={useColorModeValue('bg.default', 'bg.default')}
      minH="100vh"
      position={isCustomFullscreen ? 'fixed' : 'relative'}
      top={isCustomFullscreen ? 0 : 'auto'}
      left={isCustomFullscreen ? 0 : 'auto'}
      width={isCustomFullscreen ? '100vw' : '100%'}
      height={isCustomFullscreen ? '100vh' : 'auto'}
      zIndex={isCustomFullscreen ? 1000 : 'auto'}
      overflow={isCustomFullscreen ? 'hidden' : 'visible'}
      display="flex"
      flexDirection="column"
    >
      {!isCustomFullscreen && (
        <>
          <VStack spacing={4} align="stretch" mb={4} className="search-container">
            <VStack spacing={3} align="stretch">
              <HStack spacing={2} flexWrap={{ base: "wrap", md: "nowrap" }}>
                <Input
                  placeholder="Search for sites by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch(); }}}
                  bg={useColorModeValue('white', 'gray.700')}
                  borderColor="border.default"
                  flexGrow={1}
                  minWidth={{ base: "100%", md: "200px" }}
                />
                <HStack spacing={2} flexShrink={0}>
                  <Button
                    onClick={handleSearch}
                    isLoading={isLoading && !searchResults.length}
                    leftIcon={<MagnifyingGlass />}
                    variant="journal"
                    size={{ base: "sm", md: "md" }}
                    minWidth="80px"
                    data-testid="search-button"
                  >
                    Search
                  </Button>
                  <Button
                    leftIcon={<Icon as={Funnel} />}
                    onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                    variant="outline"
                    colorScheme="blue"
                    size={{ base: "sm", md: "md" }}
                    minWidth="120px"
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    data-testid="filter-button"
                  >
                    <Text as="span" isTruncated>
                      {isFiltersVisible ? "Hide Filters" : "Filters"}
                    </Text>
                    {activeFilterCount > 0 && <Badge ml={2} colorScheme="blue">{activeFilterCount}</Badge>}
                  </Button>
                </HStack>
              </HStack>
            </VStack>
            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
              <Box>
                <Wrap spacing={2}>
                  {filters.featureFilterLogic === 'OR' && filters.features.length > 0 && (
                     <WrapItem>
                        <Badge colorScheme="purple">Features: OR</Badge>
                      </WrapItem>
                  )}
                  {filters.countries?.map(country => (
                    <WrapItem key={country}>
                      <Tag size="sm" variant="solid" colorScheme="teal" borderRadius="full">
                        {country}
                        <Icon as={X} cursor="pointer" ml={1} boxSize={3} onClick={() => {
                          const newCountries = filters.countries.filter(c => c !== country);
                          handleFilterChange({ countries: newCountries });
                        }} />
                      </Tag>
                    </WrapItem>
                  ))}
                  {filters.era && (
                    <WrapItem>
                      <Tag size="sm" variant="solid" colorScheme="orange" borderRadius="full">
                        Era: {filters.era}
                        <Icon as={X} cursor="pointer" ml={1} boxSize={3} onClick={() => handleFilterChange({ era: '' })} />
                      </Tag>
                    </WrapItem>
                  )}
                  {filters.features.map(featureId => (
                    <WrapItem key={featureId}>
                       <Tooltip label={`Remove "${getFeatureLabel(featureId)}" filter`} placement="top" hasArrow>
                        <Tag size="sm" variant="solid" colorScheme="cyan" borderRadius="full" cursor="pointer" onClick={() => handleFeatureToggle({ id: featureId })}>
                          {getFeatureLabel(featureId)}
                          <Icon as={X} ml={1} boxSize={3} />
                        </Tag>
                      </Tooltip>
                    </WrapItem>
                  ))}
                  <WrapItem>
                    <Button size="xs" variant="ghost" colorScheme="red" onClick={resetFilters}>
                      Reset All
                    </Button>
                  </WrapItem>
                </Wrap>
              </Box>
            )}
          </VStack>
          {error && <Text color="red.500" mb={2}>{error}</Text>}
        </>
      )}
      
      {/* Desktop Filters Panel */}
      {isFiltersVisible && !isCustomFullscreen && !isMobile && (
        <Box
          position="absolute"
          top="160px" // Adjust based on header height
          left="10px"
          zIndex={20} // Higher than map controls
          bg={useColorModeValue('whiteAlpha.900', 'gray.800Alpha.900')}
          backdropFilter="blur(5px)"
          p={4}
          borderRadius="lg"
          boxShadow="xl"
          width="350px"
          maxHeight="calc(100vh - 180px)"
          overflowY="auto"
          className="custom-scrollbar"
        >
          <ElasticSearchFilters
            filters={{ ...filters, featuresMetadata }}
            onFilterChange={handleFilterChange}
            onReset={resetFilters}
            filterOptions={filterOptions}
            onInfoClick={onInfoModalOpen}
          />
        </Box>
      )}

      {/* Mobile Filters Overlay */}
      {isMobile && isFiltersVisible && (
        <>
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            zIndex={999}
            onClick={() => setIsFiltersVisible(false)}
          />
          <Box
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            bg={useColorModeValue('white', 'gray.800')}
            borderTopRadius="20px"
            zIndex={1000}
            maxHeight="85vh"
            overflow="hidden"
            boxShadow="0 -10px 25px rgba(0, 0, 0, 0.2)"
            className="mobile-filters-drawer open"
          >
            {/* Mobile Filter Header */}
            <Box
              p={4}
              borderBottom="1px solid"
              borderColor={useColorModeValue('gray.200', 'gray.600')}
              position="sticky"
              top={0}
              bg={useColorModeValue('white', 'gray.800')}
              zIndex={2}
            >
              <HStack justify="space-between" align="center">
                <Box>
                  <Heading size="md" color="text.default">
                    Filters
                  </Heading>
                  {activeFilterCount > 0 && (
                    <Text fontSize="sm" color="text.muted">
                      {activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}
                    </Text>
                  )}
                </Box>
                <HStack spacing={2}>
                  {activeFilterCount > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={resetFilters}
                    >
                      Clear All
                    </Button>
                  )}
                  <ChakraIconButton
                    aria-label="Close filters"
                    icon={<Icon as={CloseIcon} />}
                    onClick={() => setIsFiltersVisible(false)}
                    size="sm"
                    variant="ghost"
                  />
                </HStack>
              </HStack>
            </Box>

            {/* Mobile Filter Content */}
            <Box
              overflowY="auto"
              maxHeight="calc(85vh - 80px)"
              p={4}
              className="mobile-smooth-scroll"
            >
              <ElasticSearchFilters
                filters={{ ...filters, featuresMetadata }}
                onFilterChange={handleFilterChange}
                onReset={resetFilters}
                filterOptions={filterOptions}
                onInfoClick={onInfoModalOpen}
                isMobile={true}
              />
            </Box>
          </Box>
        </>
      )}

      <Box className="mobile-map-container"
        bg={useColorModeValue('map.land', 'map.land')}
        p={isCustomFullscreen ? 0 : 4}
        borderRadius={isCustomFullscreen ? 0 : "lg"}
        boxShadow={isCustomFullscreen ? "none" : "lg"}
        borderWidth={isCustomFullscreen ? 0 : "1px"}
        borderColor="border.heavy"
        position="relative"
        flexGrow={1}
        width="100%"
        height={isCustomFullscreen ? '100%' : '70vh'} // Adjusted height
      >
        {isLoading && !searchResults.length ? ( // Show spinner only if no results yet and loading
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Spinner size="xl" color={colorMode === 'dark' ? 'ancient.papyrus' : 'ancient.terracotta'} />
          </Box>
        ) : (
          <Box position="relative" h="100%">
            {/* Mobile-Optimized Compact Controls */}
            {isMobile ? (
              <>
                {/* Compact Top-Right Controls for Mobile */}
                <Box
                  position="absolute"
                  top="8px"
                  right="8px"
                  zIndex={5}
                  className="mobile-map-controls"
                >
                  <VStack spacing={1}>
                    <ChakraIconButton
                      aria-label={isCustomFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                      icon={isCustomFullscreen ? <ArrowsInSimple size={16} /> : <ArrowsOutSimple size={16} />}
                      onClick={handleToggleFullscreen}
                      size="sm"
                      className="mobile-map-control-btn"
                    />
                    <Button
                      onClick={handleMapTypeChange}
                      size="sm"
                      className="mobile-map-control-btn"
                      fontSize="8px"
                      fontWeight="bold"
                      p={0}
                    >
                      {mapTypeId === 'terrain' ? 'TER' : mapTypeId === 'satellite' ? 'SAT' : 'HYB'}
                    </Button>
                  </VStack>
                </Box>

                {/* New Map Layer Toggle */}
                <Box
                  position="absolute"
                  bottom={isMobile ? "16px" : "8px"}
                  left={isMobile ? "16px" : "8px"}
                  zIndex={5}
                  className={isMobile ? "mobile-safe-area-bottom mobile-safe-area-left" : ""}
                >
                  <MapLayerToggle
                    showLabels={showLabels}
                    onToggleLabels={handleToggleLabels}
                    showSublocations={showSublocations}
                    onToggleSublocations={() => setShowSublocations(prev => !prev)}
                    isMobile={isMobile}
                  />
                </Box>
              </>
            ) : (
              <>
                {/* Desktop Controls - Original Layout */}
                <Box
                  position="absolute"
                  bottom="20px"
                  left="20px"
                  zIndex={5}
                  bg={useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.9)')}
                  p={3}
                  borderRadius="lg"
                  boxShadow="lg"
                >
                  <VStack spacing={3} align="stretch">
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <FormLabel htmlFor="show-site-labels-toggle" mb="0" mr={2} fontSize="sm" color="text.default">
                        Show Site Labels
                      </FormLabel>
                      <Switch
                        id="show-site-labels-toggle"
                        isChecked={showLabels}
                        onChange={handleToggleLabels}
                        colorScheme="blue"
                      />
                    </FormControl>
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <FormLabel htmlFor="show-sublocations-toggle" mb="0" mr={2} fontSize="sm" color="text.default">
                        Show Sublocations
                      </FormLabel>
                      <Switch
                        id="show-sublocations-toggle"
                        isChecked={showSublocations}
                        onChange={() => setShowSublocations(prev => !prev)}
                        colorScheme="green"
                      />
                    </FormControl>
                  </VStack>
                </Box>

                <Box
                  position="absolute"
                  top="20px"
                  right="20px"
                  zIndex={5}
                  bg={useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.9)')}
                  p={2}
                  borderRadius="lg"
                  boxShadow="lg"
                >
                  <HStack spacing={2}>
                    <Button onClick={handleMapTypeChange} size="sm" variant="outline" colorScheme="gray">
                      Map: {mapTypeId.charAt(0).toUpperCase() + mapTypeId.slice(1)}
                    </Button>
                    <ChakraIconButton
                      aria-label={isCustomFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                      icon={isCustomFullscreen ? <ArrowsInSimple size={20} /> : <ArrowsOutSimple size={20} />}
                      onClick={handleToggleFullscreen}
                      size="sm"
                      variant="outline"
                      colorScheme="gray"
                    />
                  </HStack>
                </Box>
              </>
            )}

            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={searchResults.length > 0 ? 5 : 2} // Zoom in if results exist
              options={{
                ...mapOptions,
                ...(isMobile && {
                  gestureHandling: 'greedy',
                  disableDoubleClickZoom: false,
                  scrollwheel: true,
                  draggable: true,
                })
              }}
              mapTypeId={mapTypeId}
              onLoad={setMapInstance}
              onClick={handleMapClick}
            >
              <MarkerClusterer
                options={{
                  imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
                  maxZoom: 15,
                  gridSize: 60,
                  styles: [{
                    url: colorMode === 'dark' ? '/cluster-dark.png' : '/cluster-light.png',
                    height: 53,
                    width: 53,
                    textColor: colorMode === 'dark' ? '#FFFFFF' : '#000000',
                    textSize: 12
                  }]
                }}
              >
                {(clusterer) => (
                  <MemoizedMarkers
                    sites={searchResults}
                    onSiteClick={handleMarkerClick}
                    onSublocationClick={handleSublocationMarkerClick}
                    showLabels={showLabels}
                    showSublocations={showSublocations}
                    allSublocationsCache={allSublocationsCache}
                    activeFilters={filters} // Pass active filters to MemoizedMarkers
                  />
                )}
              </MarkerClusterer>
            </GoogleMap>

            {/* Info Window Display Logic */}
            {(selectedSite || selectedSublocation) && (
               <Box
                  position="absolute"
                  top={16}
                  right={4}
                  zIndex={10}
                  maxWidth="400px"
                  width="100%"
                >
                  <Box position="relative">
                    {selectedSite && selectedSite.location?.coordinates && (
                      <SiteCard
                        site={selectedSite} // Has original media
                        variant="journal"
                        showFilteredMedia={filters.features?.length > 0}
                        filteredMedia={filters.features?.length > 0 ? getFilteredMedia(selectedSite.media || []) : null}
                        filters={{ ...filters, featuresMetadata }}
                        onFeatureFilter={handleFeatureToggle}
                        isInInfoWindow={true} // Let the card know it's in a popup
                        isMobile={isMobile} // Pass mobile state to the card
                        onClose={handleMapClick} // Pass the close handler to the card
                        // onMediaClick={handleMediaClick} // Already handled by SiteCard's internal modal or passed to external
                      />
                    )}
                    {selectedSublocation && selectedSublocation.coordinates && (
                      <SubLocationInfoWindow
                        sublocation={selectedSublocation}
                        onMediaClick={handleMediaClick}
                        filters={filters}
                        featuresMetadata={featuresMetadata}
                        onFeatureFilter={handleFeatureToggle}
                        onClose={handleMapClick}
                      />
                    )}
                  </Box>
                </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Image Preview Modal */}
      {selectedImage && (
        <ImagePreviewModal
          isOpen={isImagePreviewOpen}
          onClose={handleImagePreviewClose}
          imageUrl={selectedImage.url}
          currentImage={selectedImage}
          hasMultiple={sublocationImages.length > 1}
          onPrevious={sublocationImages.length > 1 ? handlePreviousImage : null}
          onNext={sublocationImages.length > 1 ? handleNextImage : null}
        />
      )}

      {/* Feature Filter Logic Info Modal */}
      <Modal isOpen={isInfoModalOpen} onClose={onInfoModalClose} isCentered motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" /> 
        <ModalContent 
          bg={useColorModeValue('white', 'gray.800')} 
          color={useColorModeValue('gray.800', 'whiteAlpha.900')} 
          borderRadius="xl" 
          border="1px solid" 
          borderColor={useColorModeValue('gray.200', 'gray.700')}
        >
          <ModalHeader fontFamily="heading" fontWeight="bold" color={useColorModeValue('gray.700', 'white')}>Feature Filtering Logic</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <ChakraText color={useColorModeValue('gray.700', 'whiteAlpha.800')}>
                When you select multiple features to filter by, you can choose how they are combined:
              </ChakraText>
              <Box p={3} borderWidth="1px" borderRadius="md" borderColor={useColorModeValue('gray.200', 'gray.600')} bg={useColorModeValue('gray.50', 'gray.700')}>
                <ChakraHeading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>AND Logic (Default)</ChakraHeading>
                <ChakraText fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                  Shows sites where media items contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ALL</Text> of the selected features. This is more restrictive and helps you find very specific results.
                </ChakraText>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md" borderColor={useColorModeValue('gray.200', 'gray.600')} bg={useColorModeValue('gray.50', 'gray.700')}>
                <ChakraHeading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>OR Logic</ChakraHeading>
                <ChakraText fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                  Shows sites where media items contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ANY</Text> of the selected features. This is less restrictive and broadens your search.
                </ChakraText>
              </Box>
              <ChakraText fontSize="xs" color="text.subtle">
                This logic applies to the features found within individual photos, videos, or 3D models associated with a site.
              </ChakraText>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="outline" 
              colorScheme="blue"
              onClick={onInfoModalClose}
            >
              Got it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default React.memo(MapElasticPage);
